// Admin console: verification schedule (Phase 3, concept doc §7) — when each
// active tenant's media verification is next due, derived from its plan's
// verification_frequency and the last VERIFICATION audit event. Overdue
// first. This is the SCHEDULE view; actually running a VerificationJob needs
// the burn/verification pipeline (MASTER_SPEC §20), which doesn't exist yet.
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { adminApi } from '../api/adminClient';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatDate } from '../lib/format';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { Badge, Card } from '../components/ui';
import { IconCheck, IconWarning } from '../components/Icons';

export function AdminVerification() {
  const { t } = useTranslation();
  const lang = useLang();
  const schedule = useAsync(() => adminApi.adminVerificationSchedule(), []);

  return (
    <>
      <PageHeader title={t('admin.verification.title')} subtitle={t('admin.verification.subtitle')} />

      <Card>
        <ApiState status={schedule.status} error={schedule.error} data={schedule.status === 'error' ? null : schedule.data} onRetry={schedule.reload}>
          {(d) =>
            d.items.length === 0 ? (
              <EmptyState title={t('admin.verification.empty')} icon={<IconCheck width={26} height={26} />} />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>{t('common.name')}</th>
                      <th>{t('admin.fields.verificationFrequency')}</th>
                      <th>{t('admin.verification.colLast')}</th>
                      <th>{t('admin.verification.colDue')}</th>
                      <th>{t('admin.verification.colState')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((row) => (
                      <tr key={row.tenantId}>
                        <td className="cell-strong">
                          <Link to={`/customers/${encodeURIComponent(row.tenantId)}`}>{row.tenantName}</Link>
                        </td>
                        <td>
                          {row.frequency || '—'}
                          {row.intervalDays ? (
                            <span className="muted"> ({t('admin.verification.everyNDays', { count: row.intervalDays })})</span>
                          ) : null}
                        </td>
                        <td className="muted">{row.lastVerified ? formatDate(row.lastVerified, lang) : t('admin.verification.never')}</td>
                        <td className="muted">{row.dueAt ? formatDate(row.dueAt, lang) : '—'}</td>
                        <td>
                          {row.unschedulable ? (
                            <Badge tone="warning">{t('admin.verification.unschedulable')}</Badge>
                          ) : row.overdue ? (
                            <Badge tone="danger">
                              <IconWarning width={14} height={14} /> {t('admin.verification.overdue')}
                            </Badge>
                          ) : (
                            <Badge tone="success">{t('admin.verification.onTrack')}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </ApiState>
      </Card>
    </>
  );
}
